import { pickPixelRatio } from './pickPixelRatio';

/**
 * Size to hand UnrealBloomPass.setSize. That method already blurs at half
 * of what it receives, so half the drawing buffer lands the pyramid at a
 * quarter of the scene. The scene pass stays full resolution.
 */
export function bloomPassSize(cssWidth, cssHeight, pixelRatio) {
  const bufferW = cssWidth * pixelRatio;
  const bufferH = cssHeight * pixelRatio;
  return {
    width: Math.max(1, Math.round(bufferW / 2)),
    height: Math.max(1, Math.round(bufferH / 2)),
  };
}

/** Host size, pixel-ratio budget, and first-frame overview. */
export function bindSceneResize({
  container,
  camera,
  renderer,
  composer,
  bloom,
  labelRenderer,
  graph,
  voidDust,
  hostRect,
  selectedMissionIdRef,
  frameOverview,
  requestOverview,
  syncOverviewDistance,
}) {
  let lastCssW = 0;
  let lastCssH = 0;
  let lastPixelRatio = pickPixelRatio(1, 1);

  const cacheHostRect = () => {
    const r = container.getBoundingClientRect();
    hostRect.left = r.left;
    hostRect.top = r.top;
    hostRect.width = r.width;
    hostRect.height = r.height;
  };

  const setSize = () => {
    const w = Math.max(container.clientWidth, 1);
    const h = Math.max(container.clientHeight, 1);
    cacheHostRect();
    if (lastCssW > 0 && Math.abs(w - lastCssW) < 3 && Math.abs(h - lastCssH) < 3) return;
    lastCssW = w;
    lastCssH = h;
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    syncOverviewDistance();
    const ratio = pickPixelRatio(w, h, lastPixelRatio);
    lastPixelRatio = ratio;
    renderer.setPixelRatio(ratio);
    renderer.setSize(w, h, false);
    composer.setPixelRatio(ratio);
    composer.setSize(w, h);
    const bloomSize = bloomPassSize(w, h, ratio);
    bloom.setSize(bloomSize.width, bloomSize.height);
    labelRenderer.setSize(w, h);
    graph.setLineResolution(w, h);
    if (voidDust.material.uniforms?.uPixelRatio) {
      voidDust.material.uniforms.uPixelRatio.value = ratio;
    }
    const usable = w >= 32 && h >= 32;
    if (usable && (!setSize.framed || setSize.wasTiny) && !selectedMissionIdRef.current) {
      setSize.framed = true;
      setSize.wasTiny = false;
      (requestOverview || frameOverview)();
    }
    if (!usable) setSize.wasTiny = true;
  };

  setSize();
  const ro = new ResizeObserver(setSize);
  ro.observe(container);

  return {
    disposeResize: () => ro.disconnect(),
  };
}
